import React from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import SlidingPlayMain from "../../../admin/story/SlidingPlayMain";
import StoryReferenceWithGrid from "../../../admin/story/StoryReferenceWithGrid";
import SlidingPlayMistBackground from "../../../pages/puzzles/sliding/SlidingPlayMistBackground";
import ScreenshotScrambledBoard from "./ScreenshotScrambledBoard";

/**
 * App Store play shot: scrambled board on top, original picture in the tray below.
 * `hideChrome` — skip in-app header/mist when the page already has promo chrome (e.g. /05-like shots).
 * `useScrambleTiles` — CSS tile scramble with move arrows (matches /05); otherwise live SlidingPlayMain.
 */
export default function ScreenshotPlayBoardTray({
    title,
    imageUrl,
    grid,
    startingLayout,
    theme = "dark",
    hideChrome = false,
    useScrambleTiles = true,
}) {
    const light = theme === "light";
    const gridLabel = grid > 0 ? `${grid}×${grid}` : "";
    const headerColor = light ? "#0f172a" : "#e2e8f0";
    const accentColor = light ? "#15803d" : "#86efac";
    const trayBg = light ? "rgba(255, 255, 255, 0.92)" : "rgba(15, 23, 42, 0.62)";
    const trayBorder = light ? "1px solid rgba(148, 163, 184, 0.35)" : "1px solid rgba(148, 163, 184, 0.28)";
    const gutter = light ? "#e2e8f0" : "#0f172a";
    const border = light ? "rgba(15, 23, 42, 0.12)" : "rgba(248, 250, 252, 0.35)";

    const scrambled = useScrambleTiles ? (
        <ScreenshotScrambledBoard
            imageUrl={imageUrl}
            gridSize={grid}
            startingLayout={startingLayout}
        />
    ) : (
        <SlidingPlayMain
            imageUrl={imageUrl}
            gridSize={grid}
            startingLayout={startingLayout}
            readOnly
            playMoveSound={false}
            showMatchCelebrations={false}
            hideCompletedCaption
            fillParent
            maxBoardWidth="100%"
            gridGutterColor={gutter}
            gridBorderColor={border}
        />
    );

    return (
        <Box
            sx={{
                position: "relative",
                flex: 1,
                minHeight: 0,
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                isolation: "isolate",
                color: light ? "#0f172a" : "#f8fafc",
            }}
        >
            {hideChrome || light ? null : (
                <SlidingPlayMistBackground variant="night" showBubbles={false} />
            )}
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    px: hideChrome ? 0 : 1.5,
                    pt: hideChrome ? 0 : 0.5,
                    pb: hideChrome ? 0 : 1.5,
                    gap: 1.25,
                    boxSizing: "border-box",
                }}
            >
                {hideChrome ? null : (
                    <Box
                        component="header"
                        sx={{
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            minHeight: 44,
                            gap: 0.5,
                        }}
                    >
                        <ArrowBackIcon aria-hidden sx={{ fontSize: 22, color: headerColor }} />
                        <Typography
                            component="h1"
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                m: 0,
                                textAlign: "center",
                                fontWeight: 700,
                                fontSize: "1.05rem",
                                lineHeight: 1.2,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: headerColor,
                            }}
                        >
                            {title || gridLabel}
                        </Typography>
                        <Typography
                            component="span"
                            sx={{
                                flexShrink: 0,
                                minWidth: 44,
                                textAlign: "right",
                                fontWeight: 700,
                                fontSize: "0.8rem",
                                letterSpacing: "0.04em",
                                color: accentColor,
                            }}
                        >
                            {gridLabel}
                        </Typography>
                    </Box>
                )}

                <Box
                    sx={{
                        flex: "1 1 auto",
                        minHeight: 0,
                        containerType: "size",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Box
                        sx={{
                            width: "min(100cqw, 100cqh)",
                            height: "min(100cqw, 100cqh)",
                            maxWidth: "100%",
                            maxHeight: "100%",
                        }}
                    >
                        {scrambled}
                    </Box>
                </Box>

                <Box
                    sx={{
                        flexShrink: 0,
                        alignSelf: "center",
                        width: "46%",
                        maxWidth: 280,
                        borderRadius: 2,
                        bgcolor: trayBg,
                        border: trayBorder,
                        p: 1,
                        boxSizing: "border-box",
                        boxShadow: light ? "0 4px 16px rgba(15, 23, 42, 0.08)" : "none",
                    }}
                >
                    <Box sx={{ width: "100%", aspectRatio: "1 / 1" }}>
                        <StoryReferenceWithGrid
                            imageUrl={imageUrl}
                            gridSize={grid}
                            alt=""
                            squareFrame
                            fillSquareParent
                            showEmptySlot
                            showGridLines
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

ScreenshotPlayBoardTray.propTypes = {
    title: PropTypes.string,
    imageUrl: PropTypes.string,
    grid: PropTypes.number,
    startingLayout: PropTypes.arrayOf(PropTypes.number),
    theme: PropTypes.oneOf(["light", "dark"]),
    hideChrome: PropTypes.bool,
    useScrambleTiles: PropTypes.bool,
};
